import React, { useState, useMemo, useContext, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useMediaQuery } from 'react-responsive';
import { transition1 } from '../transitions';
import { CursorContext } from '../context/CursorContext';
import LazyImage from '../components/LazyImage';

import heroImg from '../img/work/cover35.jpg';

const PROPERTY_META = [
  {
    id: 'sextantio',
    title: 'Sextantio',
    location: 'Matera, Italy',
    desc: 'A restored cave hotel in the Sassi of Matera — candlelit stone rooms, preserved historic details, and the quiet, timeless atmosphere that defines the stay.',
  },
  {
    id: 'palazzo-donna-elisabetta',
    title: 'Palazzo Donna Elisabetta',
    location: 'Manduria, Italy',
    desc: 'A historic palazzo turned design-led retreat — natural light through grand interiors, layered detail, and editorial frames built for web, social, and press.',
    testimonial: {
      quote:
        'Working with Emma was a truly wonderful experience. She has an incredible eye for detail and a unique ability to capture not just images, but atmosphere and emotion.',
      body: 'What stood out most was how naturally she aligned her creative vision with ours — every shot felt intentional and fully in tune with our company’s identity. The final result exceeded our expectations and told a cohesive visual story that we’re proud to share.',
    },
  },
];

const sortEpt = (ctx) =>
  ctx
    .keys()
    .sort((a, b) => {
      const num = (key) => parseInt(key.match(/EPT-(\d+)/)?.[1] ?? '0', 10);
      return num(a) - num(b);
    })
    .map((key) => ctx(key));

const Reveal = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.7, delay: delay / 1000, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

const KickerRow = ({ label, meta }) => (
  <div className='flex items-center gap-4 mb-2'>
    <span className='text-xs tracking-[0.2em] uppercase text-[#888] font-secondary shrink-0'>
      {label}
    </span>
    <span className='flex-1 h-px bg-[#e5e5e5]' />
    {meta && (
      <span className='text-xs tracking-widest text-[#aaa] font-primary shrink-0'>{meta}</span>
    )}
  </div>
);

const Testimonial = ({ quote, body, author }) => (
  <Reveal className='max-w-5xl mx-auto text-center px-[30px] lg:px-[100px] mt-12 md:mt-16'>
    <span className='text-xs tracking-[0.2em] uppercase text-[#888] font-secondary'>
      In their words
    </span>
    <blockquote className='m-0 mt-6'>
      <p className='font-primary text-[clamp(17px,1.7vw,22px)] leading-[1.5] text-primary'>
        “{quote}”
      </p>
      {body && (
        <p className='text-[#666] text-base leading-relaxed mt-5 max-w-4xl mx-auto'>{body}</p>
      )}
    </blockquote>
    <footer className='mt-7 text-xs tracking-[0.2em] uppercase text-[#888] font-secondary'>
      {author}
    </footer>
  </Reveal>
);

// Vertical scroll is translated into horizontal movement of the image row while
// the panel is pinned; once the row is fully scrolled, normal page scroll resumes.
const STICKY_TOP = 90;

const PinnedGallery = ({ property, onOpen, scrollRef, last }) => {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const rowRef = useRef(null);
  const distanceRef = useRef(0);
  const [outerHeight, setOuterHeight] = useState(undefined);

  const measure = useCallback(() => {
    const inner = innerRef.current;
    const row = rowRef.current;
    if (!inner || !row) return;
    const distance = Math.max(0, row.scrollWidth - inner.clientWidth);
    distanceRef.current = distance;
    setOuterHeight(distance > 0 ? inner.offsetHeight + distance : undefined);
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const outer = outerRef.current;
      const row = rowRef.current;
      if (!outer || !row) return;
      const distance = distanceRef.current;
      if (distance <= 0) {
        row.style.transform = 'none';
        return;
      }
      const rel = outer.getBoundingClientRect().top - scroller.getBoundingClientRect().top;
      let p = (STICKY_TOP - rel) / distance;
      p = Math.min(1, Math.max(0, p));
      row.style.transform = `translate3d(${-p * distance}px,0,0)`;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scroller.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [scrollRef]);

  return (
    <div ref={outerRef} style={{ height: outerHeight }} className={last ? '' : 'mb-16 md:mb-24'}>
      <div
        ref={innerRef}
        className='sticky overflow-hidden'
        style={{ top: 0, paddingTop: STICKY_TOP }}
      >
        <div className='px-[30px] lg:px-[100px] mb-6'>
          <span className='text-xs tracking-[0.2em] uppercase text-[#888] font-secondary'>
            {property.location}
          </span>
          <h3 className='font-primary font-bold text-[clamp(1.5rem,3vw,2.25rem)] text-primary mt-2 mb-3 leading-tight'>
            {property.title}
          </h3>
          <p className='text-[#666] leading-relaxed max-w-2xl m-0'>{property.desc}</p>
        </div>
        <div ref={rowRef} className='flex gap-4 px-[30px] lg:px-[100px] will-change-transform'>
          {property.images.map((img, i) => (
            <div
              key={i}
              onClick={() => onOpen(property.images, i)}
              className='shrink-0 cursor-pointer'
            >
              <img
                src={img}
                alt=''
                onLoad={measure}
                className='h-[clamp(300px,50vh,600px)] w-auto object-cover'
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Hotels = () => {
  const { mouseEnterHandler, mouseLeaveHandler } = useContext(CursorContext);
  const [lightbox, setLightbox] = useState(null); // { images, index }
  const isMdOrLarger = useMediaQuery({ query: '(min-width: 768px)' });
  const scrollRef = useRef(null);

  const gallery = useMemo(
    () => sortEpt(require.context('../img/projects/stays', false, /\.(jpg|jpeg|png)$/i)),
    []
  );

  const sextantioImages = useMemo(
    () => sortEpt(require.context('../img/sextantio', false, /\.(jpg|jpeg|png)$/i)),
    []
  );

  const palazzoImages = useMemo(
    () => sortEpt(require.context('../img/palazzodonnaelisabetta', false, /\.(jpg|jpeg|png)$/i)),
    []
  );

  const properties = useMemo(
    () => [
      { ...PROPERTY_META[0], images: sextantioImages },
      { ...PROPERTY_META[1], images: palazzoImages },
    ],
    [sextantioImages, palazzoImages]
  );

  const openLightbox = (images, index) => setLightbox({ images, index });
  const closeLightbox = () => setLightbox(null);
  const showNext = () =>
    setLightbox((lb) => (lb ? { ...lb, index: (lb.index + 1) % lb.images.length } : lb));
  const showPrev = () =>
    setLightbox((lb) =>
      lb ? { ...lb, index: (lb.index - 1 + lb.images.length) % lb.images.length } : lb
    );

  useEffect(() => {
    if (!lightbox) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowRight') showNext();
      if (e.key === 'ArrowLeft') showPrev();
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox]);

  return (
    <motion.div
      ref={scrollRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={transition1}
      className='section overflow-y-auto overflow-x-hidden'
      onMouseEnter={mouseEnterHandler}
      onMouseLeave={mouseLeaveHandler}
    >
      {/* hero */}
      <section className='relative h-[78svh] min-h-[480px] overflow-hidden'>
        <img
          src={heroImg}
          alt=''
          className='w-full h-full object-cover'
        />
        <div
          className='absolute inset-0'
          style={{
            background:
              'linear-gradient(180deg,rgba(10,9,8,.28),rgba(10,9,8,0) 40%,rgba(10,9,8,.5))',
          }}
        />
        <div className='absolute left-0 right-0 bottom-[clamp(36px,6vw,72px)] px-[30px] lg:px-[100px] text-white'>
          <h1 className='font-primary font-bold text-white max-w-[15ch] mt-4 text-[clamp(20px,3.5vw,48px)] leading-[1.05] tracking-tight'>
            Photos that make people want to stay.
          </h1>
        </div>
      </section>

      {/* positioning */}
      <section className='px-[30px] lg:px-[100px] py-[clamp(50px,8vw,110px)]'>
        <div className='grid lg:grid-cols-[1fr_min(58ch,50%)] gap-[clamp(24px,6vw,90px)] items-start'>
          <Reveal>
            <span className='text-xs tracking-[0.2em] uppercase text-[#888] font-secondary'>
              The work
            </span>
          </Reveal>
          <Reveal delay={80}>
            <p className='font-secondary text-[clamp(18px,2.5vw,30px)] leading-[1.3] m-0 text-primary'>
              I help hotels, resorts and design-led properties show how a stay actually feels —
              natural light, real texture, and an editorial eye that travel brands trust.
            </p>
          </Reveal>
        </div>
      </section>

      {/* case studies */}
      <section className='bg-[#f7f7f5] py-[clamp(50px,8vw,110px)]'>
        <div className='px-[30px] lg:px-[100px] mb-12'>
          <KickerRow label='Recent clients & properties' meta='Scroll & tap to enlarge' />
        </div>
        {properties.map((p, pi) => (
          <React.Fragment key={p.id}>
            <PinnedGallery
              property={p}
              onOpen={openLightbox}
              scrollRef={scrollRef}
              last={pi === properties.length - 1 && !p.testimonial}
            />
            {p.testimonial && (
              <Testimonial
                quote={p.testimonial.quote}
                body={p.testimonial.body}
                author={p.title}
              />
            )}
          </React.Fragment>
        ))}
      </section>

      {/* gallery */}
      <section className='px-[30px] lg:px-[100px] pb-[clamp(50px,8vw,110px)] pt-[clamp(90px,14vw,200px)]'>
        <KickerRow label='Selected photos' meta='Tap to enlarge' />
        <div className='lg:columns-3 md:columns-2 columns-1 mt-9'>
          {gallery.map((imgSrc, index) => (
            <div key={index} onClick={() => openLightbox(gallery, index)} className='cursor-pointer'>
              <LazyImage src={imgSrc} className='w-full py-2' />
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className='px-[30px] lg:px-[100px] py-[clamp(50px,8vw,110px)] text-center'>
        <Reveal>
          <span className='text-xs tracking-[0.2em] uppercase text-[#888] font-secondary'>
            Now booking 2026
          </span>
          <h2 className='font-primary font-bold text-primary my-5 mx-auto max-w-[16ch] text-[clamp(30px,4.5vw,66px)] leading-[1.05] tracking-tight'>
            Let&apos;s photograph your property.
          </h2>
          <p className='text-[#666] text-lg leading-relaxed mx-auto mb-8 max-w-lg'>
            Tell me about the hotel, the dates and the spaces that matter most — I&apos;ll send
            availability and a tailored quote.
          </p>
          <Link
            to='/contact'
            className='group inline-flex items-center gap-3 font-secondary text-sm uppercase tracking-[0.2em] text-primary'
          >
            <span className='border-b border-primary/40 pb-1 transition-colors group-hover:border-primary'>
              Inquire about a shoot
            </span>
            <span
              aria-hidden='true'
              className='transition-transform duration-300 group-hover:translate-x-1'
            >
              &rarr;
            </span>
          </Link>
        </Reveal>
      </section>

      <footer className='flex w-full justify-center items-center pt-16 pb-8'>
        <div className='text-sm text-primary'>Copyright © 2026</div>
      </footer>

      {/* lightbox */}
      {lightbox && (
        <div
          className='fixed inset-0 flex justify-center items-center bg-white/90 z-50'
          style={{ height: '100vh' }}
        >
          <div className='flex justify-center items-center'>
            {lightbox.index > 0 && isMdOrLarger && (
              <button
                className='absolute left-1 md:left-3 lg:left-5 text-black text-3xl md:text-4xl font-primary focus:outline-none p-3'
                onClick={showPrev}
              >
                &larr;
              </button>
            )}
            <img
              style={{ objectFit: 'contain', maxHeight: '90vh', maxWidth: '85vw' }}
              src={lightbox.images[lightbox.index]}
              alt=''
            />
            {lightbox.index < lightbox.images.length - 1 && isMdOrLarger && (
              <button
                className='absolute right-1 md:right-3 lg:right-5 text-black text-3xl md:text-4xl font-primary focus:outline-none p-3'
                onClick={showNext}
              >
                &rarr;
              </button>
            )}
          </div>
          <button
            className='absolute left-2 md:left-3 lg:left-5 top-1 md:top-2 text-black text-5xl font-primary font-bold focus:outline-none p-3'
            onClick={closeLightbox}
          >
            &times;
          </button>
        </div>
      )}
    </motion.div>
  );
};

export default Hotels;
